import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, Eye, EyeOff, Plug, ShieldCheck, DollarSign, Sparkles, FolderPlus, Compass } from "lucide-react";
import VeyraLogo from "@/components/brand/VeyraLogo";
import { Notice } from "@/components/common/States";
import { useStudio } from "@/store/StudioContext";
import { useAuth } from "@/store/AuthContext";
import { api } from "@/lib/api";
import { notifySaved } from "@/lib/notify";
import { t, LANGUAGES, setLanguage } from "@/i18n";
import { toast } from "sonner";

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-[#f4f1ec]">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-xs text-[#a3a3a3]">{hint}</span>}
  </label>
);

const GuestOnboarding = () => {
  const { state, dispatch, refreshCatalog } = useStudio();
  const { user } = useAuth();
  const navigate = useNavigate();
  const ob = state?.onboarding || {};

  // Inicializar paso (0: Tu estudio, 1: Conectar Apimart, 2: Controlar tu gasto)
  const [step, setStep] = useState(() => Math.min(Math.max(ob.step || 0, 0), 2));
  const [name, setName] = useState(() => ob.ownerName || user?.name || "");
  const [selectedLang, setSelectedLang] = useState(() => ob.language || "es");

  // Estado del paso 2 (Clave de Apimart) - En memoria volátil únicamente, NUNCA en storage
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [keySaved, setKeySaved] = useState(false);
  const [keyError, setKeyError] = useState(null);

  // Estado del paso 3 (Presupuesto y Primer Proyecto)
  const [monthlyLimit, setMonthlyLimit] = useState("");
  const [projectName, setProjectName] = useState("");
  const [savingFinal, setSavingFinal] = useState(false);
  const [budgetError, setBudgetError] = useState(null);

  const gt = t.guestOnboarding || {
    title: "Bienvenida a VEYRA",
    subtitle: "Configura tu espacio personal de trabajo en tres sencillos pasos.",
    steps: ["Tu estudio", "Conectar Apimart", "Controlar tu gasto y empezar"],
  };

  const persistProgress = async (patch) => {
    dispatch({ type: "ONBOARDING_UPDATE", payload: patch });
    try {
      const nextOnboarding = { ...ob, ...patch };
      await api.saveState({ ...state, onboarding: nextOnboarding });
    } catch {
      // Ignorar fallos de persistencia no críticos de borrador
    }
  };

  const handleNextStep1 = async () => {
    const trimmedName = name.trim() || user?.name || "";
    await persistProgress({
      ownerName: trimmedName,
      language: selectedLang,
      step: 1,
      stepStatus: { ...ob.stepStatus, access: "completed" },
    });
    setStep(1);
    notifySaved();
  };

  const handleSaveKey = async () => {
    const cleanKey = apiKey.trim();
    if (!cleanKey) {
      setKeyError(gt.step2?.emptyKeyError || "Introduce una clave válida o pulsa «Configurar después».");
      return;
    }

    setSavingKey(true);
    setKeyError(null);
    try {
      await api.saveUserConnection({
        provider_id: "apimart",
        api_key: cleanKey,
      });
      // Descartar texto sensible de la memoria
      setApiKey("");
      setKeySaved(true);
      toast.success(gt.step2?.successSaved || "Clave de Apimart guardada y protegida en tu bóveda personal.");

      // Sincronizar catálogo y conexiones en StudioContext
      if (typeof refreshCatalog === "function") {
        await refreshCatalog().catch(() => {});
      }

      await persistProgress({
        step: 2,
        stepStatus: { ...ob.stepStatus, connections: "completed" },
      });
      setStep(2);
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("active_jobs_in_progress")) {
        setKeyError("No se puede reemplazar la clave mientras haya trabajos en curso.");
      } else if (msg.includes("vault_not_configured")) {
        setKeyError("La bóveda segura de claves no está configurada en el servidor.");
      } else {
        setKeyError(msg || "Error al conectar con la bóveda segura.");
      }
    } finally {
      setSavingKey(false);
    }
  };

  const handleSkipKey = async () => {
    setKeyError(null);
    setApiKey("");
    await persistProgress({
      step: 2,
      stepStatus: { ...ob.stepStatus, connections: "skipped" },
    });
    setStep(2);
  };

  const completeAndExit = async (thenCallback) => {
    setSavingFinal(true);
    setBudgetError(null);

    // Si el usuario introdujo un límite numérico, guardarlo en la API real de presupuesto
    const trimmedLimit = monthlyLimit.trim();
    if (trimmedLimit !== "") {
      const numLimit = Number(trimmedLimit);
      if (!Number.isFinite(numLimit) || numLimit < 0) {
        setBudgetError("El límite mensual debe ser un número válido mayor o igual a cero.");
        setSavingFinal(false);
        return;
      }
      try {
        await api.updateBudgetLimit(numLimit);
      } catch (err) {
        const msg = err.message || "";
        if (msg.includes("invalid_limit")) {
          setBudgetError("El valor de límite introducido no es válido.");
        } else if (msg.includes("limit_below_current_usage")) {
          setBudgetError("No puedes fijar un límite inferior a tus gastos actuales de este mes.");
        } else {
          setBudgetError(msg || "No se pudo actualizar el límite mensual en el servidor.");
        }
        setSavingFinal(false);
        return;
      }
    }

    // Finalizar onboarding y guardar estado en el servidor
    dispatch({ type: "ONBOARDING_COMPLETE" });
    try {
      const finalizedState = {
        ...state,
        onboarding: {
          ...ob,
          status: "completed",
          step: 2,
          stepStatus: { ...ob.stepStatus, budget: trimmedLimit ? "completed" : "skipped", first: "completed" },
        },
      };
      await api.saveState(finalizedState);
    } catch {
      // Ignorar fallo de sincronización secundario
    }

    setSavingFinal(false);
    thenCallback();
  };

  const handleCreateFirstProject = () => {
    completeAndExit(() => {
      const id = `prj_${Date.now().toString(36)}`;
      const pName = projectName.trim() || t.common?.untitled || "Mi primer proyecto";
      dispatch({
        type: "PROJECT_CREATE",
        payload: { id, name: pName, format: ob.workspacePrefs?.format || "16:9" },
      });
      navigate(`/projects/${id}/storyboard`);
    });
  };

  const handleExplore = () => {
    completeAndExit(() => {
      navigate("/");
    });
  };

  const stepsLabels = gt.steps || ["Tu estudio", "Conectar Apimart", "Controlar tu gasto y empezar"];

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-[#101010] text-[#f4f1ec]">
      <div className="mx-auto grid min-h-screen max-w-5xl grid-cols-1 md:grid-cols-[280px_1fr]">
        {/* Barra lateral / Navegación de progreso */}
        <aside className="flex flex-col border-b border-[#2b2b2b] p-6 md:border-b-0 md:border-r md:p-8">
          <VeyraLogo size={28} />
          <div className="mt-8">
            <div className="v-eyebrow mb-1 text-[#f5b544]">{gt.title}</div>
            <p className="text-xs leading-relaxed text-[#a3a3a3]">{gt.subtitle}</p>
          </div>

          <ol className="mt-8 flex flex-col gap-2" data-testid="guest-onboarding-steps">
            {stepsLabels.map((s, i) => {
              const isCurrent = i === step;
              const isPast = i < step;
              return (
                <li key={s}>
                  <div
                    data-testid={`guest-onboarding-step-${i}`}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                      isCurrent
                        ? "bg-[#1f1b14] border border-[#f5b544]/40 text-[#f5b544] font-medium"
                        : isPast
                        ? "text-[#d8d4cd]"
                        : "text-[#6b6b6b]"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-mono ${
                        isPast
                          ? "border-[#f5b544] bg-[#f5b544] text-[#1a1206]"
                          : isCurrent
                          ? "border-[#f5b544] text-[#f5b544]"
                          : "border-[#3a3a3a] text-[#6b6b6b]"
                      }`}
                    >
                      {isPast ? <Check size={12} strokeWidth={3} /> : i + 1}
                    </span>
                    <span className="truncate">{s}</span>
                  </div>
                </li>
              );
            })}
          </ol>

          <button
            type="button"
            data-testid="guest-onboarding-explore-direct"
            onClick={handleExplore}
            className="mt-auto pt-8 text-left text-xs text-[#a3a3a3] hover:text-[#f5b544] transition-colors"
          >
            {t.onboarding?.exploreWithout || "Explorar sin terminar →"}
          </button>
        </aside>

        {/* Sección de contenido del paso */}
        <section className="flex flex-col justify-between p-6 md:p-12">
          <div className="max-w-xl">
            {/* Paso 0: Tu estudio */}
            {step === 0 && (
              <div className="flex flex-col gap-6 v-fade-up" data-testid="guest-step-studio">
                <div>
                  <div className="v-eyebrow mb-1 text-[#f5b544]">Paso 1 de 3</div>
                  <h1 className="text-2xl font-semibold tracking-tight md:text-3xl text-[#f4f1ec]">
                    {gt.step1?.title || "Tu estudio"}
                  </h1>
                  <p className="mt-2 text-sm text-[#a3a3a3] leading-relaxed">
                    {gt.step1?.subtitle || "Personaliza cómo te identificas en el estudio y el idioma de tu interfaz."}
                  </p>
                </div>

                <div className="flex flex-col gap-5 rounded-xl border border-[#2b2b2b] bg-[#161616] p-5">
                  <Field label={gt.step1?.nameLabel || "Tu nombre en el estudio"}>
                    <input
                      data-testid="guest-onboarding-name"
                      className="v-input"
                      placeholder={gt.step1?.namePlaceholder || "Tu nombre o alias"}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Field>

                  <Field
                    label={gt.step1?.languageLabel || "Idioma de la interfaz"}
                    hint={gt.step1?.languageHint || "Puedes cambiar el idioma en cualquier momento desde Ajustes."}
                  >
                    <div className="flex gap-2">
                      {LANGUAGES.map((l) => (
                        <button
                          key={l.code}
                          type="button"
                          data-testid={`guest-onboarding-language-${l.code}`}
                          onClick={() => {
                            setSelectedLang(l.code);
                            setLanguage(l.code);
                            notifySaved();
                          }}
                          className={`v-btn-ghost v-btn-sm ${
                            selectedLang === l.code ? "border-[#f5b544] text-[#f5b544] bg-[#221c10]" : ""
                          }`}
                        >
                          {l.code === "es" ? "Español" : "English"}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              </div>
            )}

            {/* Paso 1: Conectar Apimart */}
            {step === 1 && (
              <div className="flex flex-col gap-6 v-fade-up" data-testid="guest-step-apimart">
                <div>
                  <div className="v-eyebrow mb-1 text-[#f5b544]">Paso 2 de 3</div>
                  <h1 className="text-2xl font-semibold tracking-tight md:text-3xl text-[#f4f1ec]">
                    {gt.step2?.title || "Conectar Apimart"}
                  </h1>
                  <p className="mt-2 text-sm text-[#a3a3a3] leading-relaxed">
                    {gt.step2?.subtitle ||
                      "Configura tu clave personal de API para generar imágenes y vídeos con tus propios créditos."}
                  </p>
                </div>

                <div className="flex flex-col gap-4 rounded-xl border border-[#2b2b2b] bg-[#161616] p-5">
                  <div className="flex items-center gap-2 text-xs text-[#a3a3a3] bg-[#1a1a1a] p-3 rounded-lg border border-[#2e2e2e]">
                    <ShieldCheck size={16} className="text-[#f5b544] shrink-0" />
                    <span>
                      {gt.step2?.help ||
                        "Tu clave se cifra y almacena de forma segura en tu bóveda personal en el servidor. Nunca se comparte con otros miembros del estudio."}
                    </span>
                  </div>

                  <Field label={gt.step2?.keyLabel || "Clave de API de Apimart"}>
                    <div className="relative flex items-center">
                      <input
                        data-testid="guest-onboarding-apimart-key"
                        type={showKey ? "text" : "password"}
                        className="v-input pr-10 font-mono text-xs"
                        placeholder={gt.step2?.keyPlaceholder || "Introduce tu API key de Apimart"}
                        value={apiKey}
                        onChange={(e) => {
                          setApiKey(e.target.value);
                          if (keyError) setKeyError(null);
                        }}
                        disabled={savingKey}
                      />
                      <button
                        type="button"
                        data-testid="guest-onboarding-toggle-key"
                        onClick={() => setShowKey((v) => !v)}
                        className="absolute right-2.5 p-1 text-[#a3a3a3] hover:text-[#f4f1ec]"
                        title={showKey ? "Ocultar clave" : "Mostrar clave"}
                      >
                        {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </Field>

                  {/* Aviso explícito de guardado != verificación */}
                  <div
                    data-testid="guest-onboarding-verify-notice"
                    className="rounded-lg border border-[#303030] bg-[#121212] p-3 text-xs text-[#d8d4cd]"
                  >
                    <span className="font-semibold text-[#f5b544]">Nota importante: </span>
                    {gt.step2?.saveNotice ||
                      "Guardar una clave no significa verificarla. La verificación se realiza al generar."}
                  </div>

                  {keyError && (
                    <Notice tone="error" testId="guest-onboarding-key-error">
                      {keyError}
                    </Notice>
                  )}
                </div>

                <div className="text-xs text-[#737373]">
                  {gt.step2?.exploreNotice ||
                    "Puedes explorar la interfaz sin clave. Las restricciones de generación se mantendrán activas hasta que configures una clave."}
                </div>
              </div>
            )}

            {/* Paso 2: Controlar tu gasto y empezar */}
            {step === 2 && (
              <div className="flex flex-col gap-6 v-fade-up" data-testid="guest-step-budget">
                <div>
                  <div className="v-eyebrow mb-1 text-[#f5b544]">Paso 3 de 3</div>
                  <h1 className="text-2xl font-semibold tracking-tight md:text-3xl text-[#f4f1ec]">
                    {gt.step3?.title || "Controlar tu gasto y empezar"}
                  </h1>
                  <p className="mt-2 text-sm text-[#a3a3a3] leading-relaxed">
                    {gt.step3?.subtitle ||
                      "Define un límite mensual personal para tus generaciones y empieza a crear."}
                  </p>
                </div>

                <div className="flex flex-col gap-5 rounded-xl border border-[#2b2b2b] bg-[#161616] p-5">
                  <Field
                    label={gt.step3?.limitLabel || "Límite mensual (USD)"}
                    hint={
                      gt.step3?.limitHint ||
                      "Puedes configurarlo después. Para generar necesitarás una clave de Apimart y un límite mensual."
                    }
                  >
                    <div className="relative flex items-center">
                      <span className="absolute left-3 text-sm text-[#737373]">$</span>
                      <input
                        data-testid="guest-onboarding-budget-limit"
                        type="number"
                        min="0"
                        step="1"
                        className="v-input pl-7"
                        placeholder={gt.step3?.limitPlaceholder || "Ej: 50.00"}
                        value={monthlyLimit}
                        onChange={(e) => {
                          setMonthlyLimit(e.target.value);
                          if (budgetError) setBudgetError(null);
                        }}
                        disabled={savingFinal}
                      />
                    </div>
                  </Field>

                  <Field
                    label={gt.step3?.projectLabel || "Nombre de tu primera producción"}
                    hint="Opcional. Se creará un proyecto vacío con el Storyboard listo para trabajar."
                  >
                    <input
                      data-testid="guest-onboarding-project-name"
                      className="v-input"
                      placeholder={gt.step3?.projectPlaceholder || "Mi primer proyecto"}
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      disabled={savingFinal}
                    />
                  </Field>

                  {budgetError && (
                    <Notice tone="error" testId="guest-onboarding-budget-error">
                      {budgetError}
                    </Notice>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Barra inferior de navegación */}
          <div className="mt-8 flex max-w-xl items-center justify-between border-t border-[#222] pt-6">
            <button
              type="button"
              data-testid="guest-onboarding-back"
              disabled={step === 0 || savingKey || savingFinal}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="v-btn-ghost flex items-center gap-1.5 disabled:opacity-30"
            >
              <ChevronLeft size={16} />
              {t.common?.back || "Atrás"}
            </button>

            <div className="flex items-center gap-3">
              {step === 0 && (
                <button
                  type="button"
                  data-testid="guest-onboarding-next"
                  onClick={handleNextStep1}
                  className="v-btn-primary"
                >
                  {t.common?.next || "Continuar"}
                </button>
              )}

              {step === 1 && (
                <>
                  <button
                    type="button"
                    data-testid="guest-onboarding-skip-key"
                    disabled={savingKey}
                    onClick={handleSkipKey}
                    className="v-btn-ghost text-xs text-[#a3a3a3] hover:text-[#f4f1ec]"
                  >
                    {gt.step2?.skipBtn || "Configurar después"}
                  </button>
                  <button
                    type="button"
                    data-testid="guest-onboarding-save-key"
                    disabled={savingKey || !apiKey.trim()}
                    onClick={handleSaveKey}
                    className="v-btn-primary"
                  >
                    {savingKey ? "Guardando..." : gt.step2?.saveBtn || "Guardar clave y continuar"}
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <button
                    type="button"
                    data-testid="guest-onboarding-explore"
                    disabled={savingFinal}
                    onClick={handleExplore}
                    className="v-btn-ghost flex items-center gap-1.5"
                  >
                    <Compass size={14} />
                    {gt.step3?.exploreBtn || "Explorar VEYRA"}
                  </button>
                  <button
                    type="button"
                    data-testid="guest-onboarding-create-project"
                    disabled={savingFinal}
                    onClick={handleCreateFirstProject}
                    className="v-btn-primary flex items-center gap-1.5"
                  >
                    <FolderPlus size={14} />
                    {savingFinal ? "Creando..." : gt.step3?.createProjectBtn || "Crear mi primer proyecto"}
                  </button>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default GuestOnboarding;
